import { createStyles } from '@mantine/core';

import GamecenterEntry from '../../../common/components/GamecenterEntriesPageScheduled/GamecenterEntry/GamecenterEntry';
import type { ScheduledGamecenterEntryProps } from '../../../common/components/GamecenterEntriesPageScheduled/GamecenterEntriesPageScheduled';
import { TeamSurvivorEntryDisplay } from '../TeamSurvivorEntryDisplay/TeamSurvivorEntryDisplay';
import {
  getAnchorSlate,
  getSlatePickState,
} from '../../../common/components/GamecenterEntriesPageScheduled/GamecenterEntry/entryPickState';

const useStyles = createStyles((theme) => ({
  slates: {
    display: 'flex',
    gap: theme.other.spacing._8,
    margin: `0 -${theme.other.spacing._16}px -${theme.other.spacing._16}px`,
    padding: `${theme.other.spacing._8}px ${theme.other.spacing._16}px ${theme.other.spacing._16}px`,
    overflow: 'auto',
  },
}));

function TeamSurvivorEntryScheduled({
  contestId,
  entry,
  openEntryNameEditModal,
}: {
  contestId: string;
  entry: ScheduledGamecenterEntryProps;
  openEntryNameEditModal: (entry: { id: string; name: string }) => void;
}) {
  const { classes } = useStyles();

  const canMakePicks = entry.pickState.kind === 'controller';
  const anchorSlate = getAnchorSlate(entry.slates);
  if (!anchorSlate) return null;

  const requiredPicksCount = anchorSlate.requiredPicksCount;

  return (
    <GamecenterEntry
      key={entry.id}
      contestId={contestId}
      entryId={entry.id}
      name={entry.name}
      isMultiOwner={entry.isMultiOwner}
      pickState={entry.pickState}
      requiredPicksCount={requiredPicksCount}
      openEntryNameEditModal={openEntryNameEditModal}
    >
      <div className={classes.slates}>
        {entry.slates.map((slate, slateIndex) => {
          const slatePickState = getSlatePickState(entry.pickState, slate);
          const isAnchorSlate = slate === anchorSlate;
          const slateHasVisiblePicks = slate.picks?.some((pick) => pick != null) ?? false;
          const isSlateMissingPicks =
            isAnchorSlate &&
            !slateHasVisiblePicks &&
            (slatePickState.kind === 'controller'
              ? slatePickState.isMissingPicks
              : slatePickState.kind === 'readOnlyMissing');
          const slateHasHiddenPicks = slatePickState.kind === 'readOnlyHidden';
          const slateRequiredPicksCount = slate.requiredPicksCount;

          // Convert slate to the format expected by TeamSurvivorEntryDisplay
          const displaySlate = {
            id: slate.id,
            name: slate.name,
            picks: slate.picks?.filter((pick) => !!pick) || [],
            isMakePicks: slate.isMakePicks,
          };

          return (
            <TeamSurvivorEntryDisplay
              key={slate.id}
              contestId={contestId}
              entryId={entry.id}
              slate={displaySlate}
              slateIndex={slateIndex}
              isSlateMissingPicks={isSlateMissingPicks}
              requiredPicksCount={slateRequiredPicksCount}
              isLinkEnabled={canMakePicks}
              canMakePicks={canMakePicks}
              hasHiddenPicks={slateHasHiddenPicks}
            />
          );
        })}
      </div>
    </GamecenterEntry>
  );
}

export default TeamSurvivorEntryScheduled;
